import type { EquipmentCatalogEntry, Supplier } from '../../types/supplyChain';
import { SUPPLIER_BASELINES } from '../supplyChainBaselines';
import {
  OEM_GENERATION_SOURCE_REGISTER,
  assessOemProductFootprint,
  preferredOemPerformanceCase,
} from '../generationImpact/generationOemSourceRegister';
import { supplierIdsForOemManufacturer } from './oemGenerationSupplierLinks';

function sourceLeadTime(
  supplierIds: readonly string[],
  suppliers: readonly Supplier[],
): { low: number; high: number } | undefined {
  return supplierIds
    .map(id => suppliers.find(supplier => supplier.id === id)?.typicalLeadMonths)
    .find((value): value is { low: number; high: number } => value != null);
}

function frequencyLabel(frequencyHz: readonly number[]): string {
  return frequencyHz.length === 0 ? 'frequency not stated' : `${frequencyHz.join('/')} Hz`;
}

export function oemGenerationEquipmentCatalogEntries(
  suppliers: readonly Supplier[] = SUPPLIER_BASELINES,
): EquipmentCatalogEntry[] {
  const generationEntries = OEM_GENERATION_SOURCE_REGISTER.products.map(product => {
    const performance = preferredOemPerformanceCase(product);
    const footprint = assessOemProductFootprint(product);
    const supplierIds = [...supplierIdsForOemManufacturer(product.manufacturer)];
    const leadTimeMonths = sourceLeadTime(supplierIds, suppliers);
    const footprintText = footprint.lengthM != null && footprint.widthM != null
      ? `${footprint.lengthM} × ${footprint.widthM} m ${footprint.authority.replaceAll('-', ' ')}`
      : 'no bounded public footprint';
    const technologyTag = product.technology.includes('turbine')
      ? 'gas-turbine'
      : 'recip-engine';
    const netElectricalCapacity = performance.ratingScope === 'generator-terminals'
      || performance.ratingScope === 'package-net'
      || performance.ratingScope === 'plant-net';
    return {
      id: `catalog-oem-generation-${product.id}`,
      name: `${product.manufacturer} ${product.model}`,
      spec: [
        product.variant,
        product.technology.replaceAll('-', ' '),
        `${performance.electricalOutputKw / 1_000} MW ${performance.ratingScope.replaceAll('-', ' ')}`,
        `${performance.duty.replaceAll('-', ' ')} · ${frequencyLabel(performance.frequencyHz)}`,
        footprintText,
        'OEM technical evidence; price and project lead time require a quote',
      ].join(' · '),
      scmCategory: 'power-generation' as const,
      tags: [
        'oem-source-register',
        technologyTag,
        product.technology,
        product.manufacturer.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-'),
        product.model.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-'),
      ],
      supplierIds,
      ...(leadTimeMonths ? { leadTimeMonths } : {}),
      capacityRating: `${performance.electricalOutputKw} kW (${performance.ratingScope})`,
      ...(netElectricalCapacity
        ? { powerPerUnitMw: performance.electricalOutputKw / 1_000 }
        : {}),
      lastVerifiedAt: OEM_GENERATION_SOURCE_REGISTER.compiledAt,
      source: 'oem-source-register' as const,
      oemGenerationProductId: product.id,
      oemPerformanceCaseId: performance.id,
    };
  });

  const electricalEntries = OEM_GENERATION_SOURCE_REGISTER.electricalMachines.map(machine => {
    const rating = machine.ratingCases.find(item => item.id === machine.preferredRatingCaseId);
    if (!rating) {
      throw new RangeError(`${machine.id} has no preferred electrical-machine rating case`);
    }
    const supplierIds = [...supplierIdsForOemManufacturer(machine.manufacturer)];
    const leadTimeMonths = sourceLeadTime(supplierIds, suppliers);
    return {
      id: `catalog-oem-electrical-${machine.id}`,
      name: `${machine.manufacturer} ${machine.model}`,
      spec: [
        machine.variant,
        `${rating.ratedKva} kVA · ${rating.ratedVoltageKv} kV · ${rating.frequencyHz} Hz`,
        `${rating.poles}-pole · ${rating.ratedSpeedRpm} rpm · ${rating.ratedPowerFactor == null ? 'pf not stated' : `pf ${rating.ratedPowerFactor}`}`,
        'Exact OEM rating; coupling compatibility and project lead time require confirmation',
      ].join(' · '),
      scmCategory: 'power-conversion' as const,
      tags: [
        'oem-source-register',
        'alternator',
        'synchronous-generator',
        machine.manufacturer.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-'),
        machine.model.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-'),
      ],
      supplierIds,
      ...(leadTimeMonths ? { leadTimeMonths } : {}),
      capacityRating: `${rating.ratedKva} kVA`,
      lastVerifiedAt: OEM_GENERATION_SOURCE_REGISTER.compiledAt,
      source: 'oem-source-register' as const,
      oemElectricalMachineId: machine.id,
      oemElectricalRatingCaseId: rating.id,
    };
  });

  return [...generationEntries, ...electricalEntries];
}
